/**
 * SettingsSidebar - Resizable right sidebar for Game, Audio, Display, and Debug settings.
 * Drop-in replacement for SettingsPanel with identical API:
 *   open(tab), close(), isOpen(), getTabContainer(tabName), destroy()
 *
 * Adapted from windowlite's Sidebar class.
 */

const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 280;
const MAX_WIDTH = 500;

export default class SettingsSidebar {
  constructor() {
    this._isOpen = false;
    this._activeTab = 'game';
    this._width = DEFAULT_WIDTH;
    this.tabContainers = {};
    this.tabButtons = {};

    // Resize state
    this._isResizing = false;
    this._resizePointerId = null;
    this._resizeStartX = 0;
    this._resizeStartWidth = 0;

    this._createDOM();
    this._setupEvents();
    SettingsSidebar._installGlobalResizeHandlers();
  }

  _createDOM() {
    // Sidebar element
    this.element = document.createElement('div');
    this.element.className = 'gw-sidebar gw-sidebar-right';
    this.element.style.width = `${this._width}px`;
    this.element.style.zIndex = '3500';

    // Header
    const header = document.createElement('div');
    header.className = 'gw-sidebar-header';

    const titleContainer = document.createElement('div');
    titleContainer.className = 'gw-sidebar-title-container';

    const titleEl = document.createElement('div');
    titleEl.className = 'gw-sidebar-title';
    titleEl.textContent = 'Settings';
    titleContainer.appendChild(titleEl);

    const controls = document.createElement('div');
    controls.className = 'gw-sidebar-controls';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'gw-close-btn';
    closeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>';
    closeBtn.setAttribute('aria-label', 'Close settings');
    closeBtn.setAttribute('type', 'button');
    closeBtn.addEventListener('click', () => this.close());
    controls.appendChild(closeBtn);

    header.appendChild(titleContainer);
    header.appendChild(controls);

    // Tab bar
    const tabBar = document.createElement('div');
    tabBar.className = 'gw-sidebar-tab-bar';

    const tabs = [
      { id: 'game', label: 'Game' },
      { id: 'audio', label: 'Audio' },
      { id: 'display', label: 'Display' },
      { id: 'debug', label: 'Debug' },
    ];

    tabs.forEach(tab => {
      const btn = document.createElement('button');
      btn.className = 'gw-sidebar-tab' + (tab.id === this._activeTab ? ' active' : '');
      btn.textContent = tab.label;
      btn.addEventListener('click', () => this._switchTab(tab.id));
      tabBar.appendChild(btn);
      this.tabButtons[tab.id] = btn;
    });

    // Body (scrollable content area)
    const body = document.createElement('div');
    body.className = 'gw-sidebar-body';

    tabs.forEach(tab => {
      const container = document.createElement('div');
      container.className = 'gw-sidebar-tab-content' + (tab.id === this._activeTab ? ' active' : '');
      container.id = `settings-${tab.id}`;
      body.appendChild(container);
      this.tabContainers[tab.id] = container;
    });

    // Resize handle (left edge)
    this._resizeHandle = document.createElement('div');
    this._resizeHandle.className = 'gw-sidebar-resize-handle';
    this._resizeHandle.style.touchAction = 'none';
    this._resizeHandle.addEventListener('pointerdown', (e) => this._startResize(e), { passive: false });

    this.element.appendChild(this._resizeHandle);
    this.element.appendChild(header);
    this.element.appendChild(tabBar);
    this.element.appendChild(body);

    // Prevent wheel events from reaching game zoom handler
    this.element.addEventListener('wheel', (e) => {
      e.stopPropagation();
    }, { passive: false });

    // Stop touch events from propagating to camera controller
    this.element.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });
    this.element.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, { passive: true });

    // Focus on interaction — raises sidebar above toolbar
    this.element.addEventListener('pointerdown', () => this.focus(), { passive: true });

    // Append to #container so everything shares the same stacking context
    const container = document.getElementById('container');
    (container || document.body).appendChild(this.element);
  }

  _setupEvents() {
    this._onKeyDown = (e) => {
      if (e.key === 'Escape' && this._isOpen) {
        e.stopPropagation();
        this.close();
      }
    };
    document.addEventListener('keydown', this._onKeyDown);
  }

  _switchTab(tabId) {
    if (this._activeTab === tabId) return;
    this._activeTab = tabId;
    window.game?.audioSystem?.audio?.playUiClick?.();

    Object.keys(this.tabButtons).forEach(id => {
      this.tabButtons[id].classList.toggle('active', id === tabId);
    });
    Object.keys(this.tabContainers).forEach(id => {
      this.tabContainers[id].classList.toggle('active', id === tabId);
    });
  }

  // ---- Resize ----

  static _installGlobalResizeHandlers() {
    if (SettingsSidebar._resizeHandlersInstalled) return;
    SettingsSidebar._resizeHandlersInstalled = true;
    SettingsSidebar._activeResize = null;

    document.addEventListener('pointermove', (e) => {
      const a = SettingsSidebar._activeResize;
      if (!a) return;
      if (a.pointerId != null && e.pointerId != null && e.pointerId !== a.pointerId) return;
      a.sidebar._resize(e);
    }, { passive: true });

    document.addEventListener('pointerup', (e) => {
      const a = SettingsSidebar._activeResize;
      if (!a) return;
      if (a.pointerId != null && e.pointerId != null && e.pointerId !== a.pointerId) return;
      a.sidebar._stopResize();
      SettingsSidebar._activeResize = null;
    }, { passive: true });

    document.addEventListener('pointercancel', () => {
      const a = SettingsSidebar._activeResize;
      if (!a) return;
      a.sidebar._stopResize();
      SettingsSidebar._activeResize = null;
    }, { passive: true });
  }

  _startResize(e) {
    if (e.button !== 0) return;
    e.preventDefault();

    this._isResizing = true;
    this._resizePointerId = e.pointerId ?? null;
    this._resizeStartX = e.clientX;
    this._resizeStartWidth = this._width;

    SettingsSidebar._activeResize = { sidebar: this, pointerId: this._resizePointerId };

    this.element.classList.add('gw-sidebar-resizing');
    this._resizeHandle.classList.add('gw-resizing');
    document.body.style.cursor = 'ew-resize';

    if (this._resizePointerId != null && this._resizeHandle.setPointerCapture) {
      try { this._resizeHandle.setPointerCapture(this._resizePointerId); } catch {}
    }
  }

  _resize(e) {
    if (!this._isResizing) return;
    // Right sidebar: moving left increases width
    const delta = this._resizeStartX - e.clientX;
    const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, this._resizeStartWidth + delta));
    this._width = newWidth;
    this.element.style.width = `${newWidth}px`;
  }

  _stopResize() {
    if (!this._isResizing) return;
    this._isResizing = false;
    this.element.classList.remove('gw-sidebar-resizing');
    this._resizeHandle.classList.remove('gw-resizing');
    document.body.style.cursor = '';

    if (this._resizePointerId != null && this._resizeHandle.releasePointerCapture) {
      try { this._resizeHandle.releasePointerCapture(this._resizePointerId); } catch {}
    }
    this._resizePointerId = null;
  }

  // ---- Focus ----

  focus() {
    // Blur all windows in the same container
    const parent = this.element.parentNode;
    if (parent) {
      parent.querySelectorAll('.gw-window').forEach(w => {
        w.classList.remove('gw-focused');
        w.style.zIndex = '3000';
      });
    }
    this.element.classList.add('gw-focused');
    this.element.style.zIndex = '10150';
  }

  blur() {
    this.element.classList.remove('gw-focused');
    this.element.style.zIndex = '3500';
  }

  // ---- Public API (SettingsPanel-compatible) ----

  open(tab) {
    if (tab) this._switchTab(tab);
    this._isOpen = true;
    this.element.classList.add('gw-sidebar-open');
    this.focus();
    window.game?.audioSystem?.audio?.playUiOpen?.();
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.element.classList.remove('gw-sidebar-open');
    window.game?.audioSystem?.audio?.playUiClose?.();
  }

  isOpen() {
    return this._isOpen;
  }

  getTabContainer(tabName) {
    return this.tabContainers[tabName];
  }

  destroy() {
    document.removeEventListener('keydown', this._onKeyDown);
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}
